import { useEffect, useState } from 'react'
import { Bell, X } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import { getPushState, enablePushNotifications } from '../../services/push'

const DONT_ASK_KEY = 'push_prompt_dont_ask'

/**
 * Post-login prompt asking once whether to enable push notifications.
 *
 * Shows only when Web Push is supported, not yet enabled and permission was
 * never denied. "Don't ask again" persists in localStorage; the Profile page's
 * Push Notifications card remains the always-available entry point.
 */
export default function PushEnablePrompt() {
    const [visible, setVisible] = useState(false)
    const [busy, setBusy] = useState(false)
    const toast = useToast()

    useEffect(() => {
        let active = true
        const timer = setTimeout(async () => {
            try {
                if (localStorage.getItem(DONT_ASK_KEY)) return
                const state = await getPushState()
                // Show only for the pristine case: supported, off, never answered.
                if (active && state.supported && !state.enabled && state.permission === 'default') {
                    setVisible(true)
                }
            } catch {
                // Push unavailable (insecure origin etc.) — stay quiet.
            }
        }, 2500) // let the post-login page transition settle first
        return () => { active = false; clearTimeout(timer) }
    }, [])

    if (!visible) return null

    const handleEnable = async () => {
        setBusy(true)
        try {
            const result = await enablePushNotifications()
            if (result.ok) {
                toast.success('Push notifications enabled')
            } else if (result.reason === 'denied') {
                toast.error('Permission denied — enable notifications for this site in your browser settings')
            } else {
                toast.error('Could not enable push notifications')
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not enable push notifications')
        } finally {
            setBusy(false)
            setVisible(false)
        }
    }

    const handleDontAsk = () => {
        localStorage.setItem(DONT_ASK_KEY, '1')
        setVisible(false)
    }

    return (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-bg-elevated border border-border-light rounded-xl shadow-2xl p-4">
            <button
                onClick={() => setVisible(false)}
                aria-label="Dismiss"
                className="absolute top-2 right-2 p-1 text-text-muted hover:text-text-lighter transition-colors"
            >
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
                <Bell className="w-5 h-5 text-accent-blue mt-0.5 flex-shrink-0" />
                <div>
                    <h3 className="text-sm font-semibold">Enable push notifications?</h3>
                    <p className="text-xs text-text-muted mt-1">
                        Get notified when a scav run, craft or room production finishes — even when the app is closed.
                    </p>
                </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
                <button
                    onClick={handleDontAsk}
                    className="px-3 py-1.5 text-xs text-text-muted hover:text-text-lighter transition-colors"
                >
                    Don't ask again
                </button>
                <button
                    onClick={() => setVisible(false)}
                    className="px-3 py-1.5 text-xs font-semibold bg-bg-hover border border-border-light rounded hover:bg-bg-active transition-colors text-text-lighter"
                >
                    Not now
                </button>
                <button
                    onClick={handleEnable}
                    disabled={busy}
                    className="px-3 py-1.5 text-xs font-semibold bg-accent-blue text-white rounded hover:bg-accent-blue-dark transition-colors disabled:opacity-60"
                >
                    {busy ? 'Enabling…' : 'Enable'}
                </button>
            </div>
        </div>
    )
}
